/**
 * WebRTC for acequia in Node (publish-the-documentation project, phase 3).
 *
 * Node has no RTCPeerConnection; `node-datachannel` supplies one —
 * libdatachannel bindings with prebuilt binaries, MPL-2.0, about 1 MB, a
 * plain npm dependency (yarn install: the owner's decision of 2026-09-28,
 * after a vendored-on-the-CDN detour). It is an optionalDependency so an
 * install never fails on a platform without a prebuilt binary; there the
 * relay-proxied path still works and a peer connection is refused with a
 * reason. The pure-JavaScript alternative, werift, was tried the same day:
 * its ICE and channel work against Chrome, but simple-peer never sees a
 * selected candidate pair in its stats and so never reports `connect`.
 *
 * ACEQUIA_NODE_WEBRTC_ENABLED=0 turns WebRTC off. What this returns is what
 * simple-peer's `wrtc` option wants — RTCPeerConnection,
 * RTCSessionDescription, RTCIceCandidate — shaped as a browser shapes them,
 * and installed as globals for the code that reaches for them by name (the
 * TURN self-test).
 */

const BACKENDS = ['node-datachannel']

// The specifier is built, not written, so the browser bundler leaves it
// alone: this module rides along in acequia.js (deps.js imports it inside
// the Node branch) and must not drag the native package's code, or Node's
// built-ins, into a browser.
const POLYFILL = ['node-datachannel', 'polyfill'].join('/')

async function importBackend(name) {
    if (name === 'node-datachannel') {
        const p = await import(/* webpackIgnore: true */ POLYFILL)
        return p.default?.RTCPeerConnection ? p.default : p
    }
    throw new Error(`acequia webrtc: unknown backend "${name}" (known: ${BACKENDS.join(', ')})`)
}

/**
 * The W3C shape simple-peer expects over the backend (found 2026-09-28
 * against a browser peer):
 *   - createOffer/createAnswer return PLAIN {type, sdp}: simple-peer assigns
 *     to `offer.sdp` (trickle filtering, sdpTransform), and both backends'
 *     description objects have a getter-only `sdp`. The set*Description
 *     methods take the plain object.
 *   - RTCSessionDescription takes the {type, sdp} dictionary.
 *   - a data channel's `onbufferedamountlow` is a property simple-peer
 *     assigns for backpressure; werift only has its own event object, so
 *     its channel class gets the accessor (node-datachannel has it).
 */
export function shape(mod) {
    class RTCSessionDescription {
        constructor(init = {}) {
            this.type = init.type
            this.sdp = init.sdp
        }
        toJSON() {
            return { type: this.type, sdp: this.sdp }
        }
    }
    const plain = (d) => (d ? { type: d.type, sdp: d.sdp } : d)
    const hasAccessor = (obj, key) => {
        for (let o = obj; o; o = Object.getPrototypeOf(o)) {
            const d = Object.getOwnPropertyDescriptor(o, key)
            if (d) return !!(d.set || d.get)
        }
        return false
    }
    // werift's RTCDataChannel has no `onbufferedamountlow` property, only its
    // own event object: give the CLASS the accessor once, so channels made
    // either way (createDataChannel, ondatachannel) carry it.
    const ChannelProto = mod.RTCDataChannel?.prototype
    if (ChannelProto && !hasAccessor(ChannelProto, 'onbufferedamountlow')) {
        const handlers = new WeakMap()
        Object.defineProperty(ChannelProto, 'onbufferedamountlow', {
            get() {
                return handlers.get(this) || null
            },
            set(fn) {
                const first = !handlers.has(this)
                handlers.set(this, typeof fn === 'function' ? fn : null)
                if (first && this.bufferedAmountLow?.subscribe) {
                    this.bufferedAmountLow.subscribe(() => handlers.get(this)?.())
                }
            },
            configurable: true,
        })
    }
    const Base = mod.RTCPeerConnection
    class RTCPeerConnection extends Base {
        async createOffer(options) {
            return plain(await super.createOffer(options))
        }
        async createAnswer(options) {
            return plain(await super.createAnswer(options))
        }
        setLocalDescription(description) {
            return super.setLocalDescription(plain(description))
        }
        setRemoteDescription(description) {
            return super.setRemoteDescription(plain(description))
        }
    }
    return { RTCPeerConnection, RTCSessionDescription, RTCIceCandidate: mod.RTCIceCandidate }
}

let loaded = null

/**
 * The `wrtc` object for simple-peer, from the first backend that loads
 * (or the one ACEQUIA_NODE_WEBRTC names), also installed on globalThis.
 * Loaded once per process.
 */
export async function loadNodeWebRTC() {
    if (loaded) return loaded
    const wanted = process.env.ACEQUIA_NODE_WEBRTC
    const order = wanted ? [wanted] : BACKENDS
    const failures = []
    for (const name of order) {
        let mod
        try {
            mod = await importBackend(name)
        } catch (e) {
            failures.push(`${name}: ${e.message.split('\n')[0]}`)
            continue
        }
        const wrtc = shape(mod)
        for (const k of ['RTCPeerConnection', 'RTCSessionDescription', 'RTCIceCandidate']) {
            if (typeof globalThis[k] === 'undefined') globalThis[k] = wrtc[k]
        }
        loaded = { ...wrtc, backend: name }
        return loaded
    }
    throw new Error(`no WebRTC backend loaded (${failures.join('; ')}) — yarn add node-datachannel`)
}
